import PropTypes from "prop-types";
import SideBarItem from "./side-bar-item";
import { logoutUser } from "actions/auth";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UserWelcome from "./user-welcome";
import "./side-bar.scss";
import useTranslate from "hooks/use-translate-language";
import { useState, useEffect, useRef } from "react";
import MenuList from "@mui/material/MenuList";
import { filterItems } from "./menu-items-info";
import selectors from "selectors";

const SideBar = ({ window, mobileOpen, onDrawerToggle }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const translate = useTranslate();
  const permissions = useSelector(selectors.getAppPermissions);
  const [selected, setSelected] = useState(0);
  const [subMenu, setSubMenu] = useState(false);
  const [subMenuParent, setSubMenuParent] = useState("");
  const closeButtonRef = useRef(null);

  const menuItems = filterItems(permissions);

  const container =
    window !== undefined ? () => window().document.body : undefined;

  // Focus management for mobile drawer
  useEffect(() => {
    if (mobileOpen && closeButtonRef.current) {
      // When drawer opens, focus the close button
      setTimeout(() => {
        closeButtonRef.current?.focus();
      }, 100);
    }
  }, [mobileOpen]);

  // Keyboard support for Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && mobileOpen) {
        onDrawerToggle();
      }
    };

    if (mobileOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [mobileOpen, onDrawerToggle]);

  const keyDown = e => {
    e.stopPropagation();

    switch (e.code) {
      case "ArrowUp":
        e.preventDefault();
        previous(e.target.id);
        break;
      case "ArrowDown":
        e.preventDefault();
        next(e.target.id);
        break;
      case "ArrowRight": {
        const p = menuItems.find(x => x.itemId === e.target.id);
        if (p && p.collapsable) {
          setSubMenu(true);
          setSubMenuParent(e.target.id);
          if (document.getElementById(p.subMenuItems[0].itemId) === null) {
            document.getElementById(p.itemId).click();
          } else {
            document.getElementById(p.subMenuItems[0].itemId).focus();
          }
        }
        break;
      }
      case "ArrowLeft":
        if (subMenu) {
          document.getElementById(subMenuParent).focus();
          setSubMenu(false);
          setSubMenuParent("");
        }
        break;
      case "Enter":
      case "Space": {
        let foundItem = menuItems.find(x => x.itemId === e.target.id);
        if (!foundItem) {
          for (const mainItem of menuItems) {
            if (mainItem.subMenuItems) {
              const subItem = mainItem.subMenuItems.find(
                x => x.itemId === e.target.id
              );
              if (subItem) {
                // Close mobile drawer after sub-menu navigation
                if (mobileOpen) {
                  onDrawerToggle();
                }
                setTimeout(() => {
                  const mainContent = document.getElementById("main-content");
                  if (mainContent) {
                    const firstButton = mainContent.querySelector(
                      "button:not([disabled])"
                    );
                    if (firstButton) {
                      firstButton.focus();
                    }
                  }
                }, 100);
                return;
              }
            }
          }
        }
        break;
      }
      default:
        break;
    }
  };

  const next = id => {
    let nextId = 0;
    if (subMenu) {
      const itm = menuItems.find(x => x.itemId === subMenuParent);
      const inx = itm.subMenuItems.findIndex(x => x.itemId === id);
      nextId =
        inx + 1 < itm.subMenuItems.length
          ? itm.subMenuItems[inx + 1].itemId
          : itm.subMenuItems[inx].itemId;
    } else {
      const currentIndex = menuItems.findIndex(x => x.itemId === id);
      nextId =
        currentIndex + 1 < menuItems.length
          ? menuItems[currentIndex + 1].itemId
          : menuItems[currentIndex].itemId;
      setSelected(nextId);
    }

    document.getElementById(nextId).focus();
  };

  const previous = id => {
    let nextId = 0;
    if (subMenu) {
      const itm = menuItems.find(x => x.itemId === subMenuParent);
      const inx = itm.subMenuItems.findIndex(x => x.itemId === id);
      nextId =
        inx - 1 >= 0
          ? itm.subMenuItems[inx - 1].itemId
          : itm.subMenuItems[inx].itemId;
    } else {
      const ind = menuItems.findIndex(x => x.itemId === id);
      nextId =
        ind - 1 >= 0
          ? menuItems[ind - 1].itemId
          : menuItems[ind].itemId;
      setSelected(nextId);
    }

    document.getElementById(nextId).focus();
  };

  const handleItemClick = location => {
    history.push(location);
  };

  const drawer = (
    <div className="side-bar">
      <div className="side-bar-content">
        <div className="mobile-close-button">
          <IconButton
            ref={closeButtonRef}
            onClick={onDrawerToggle}
            aria-label="Close menu"
            sx={{ display: { sm: 'none' }, color: '#05294a' }}
          >
            <CloseIcon />
          </IconButton>
        </div>
        <UserWelcome />
        <h2 id="mobile-menu-title" className="sr-only">Navigation Menu</h2>
          <MenuList
            aria-label="SideBar menu"
            id="side-bar-menu"
            onKeyDown={e => {
              keyDown(e);
            }}
          >
            {menuItems &&
              menuItems.map((i, index) => (
                <SideBarItem
                  key={index}
                  id={i.itemId}
                  text={translate(i.itemText)}
                  onClick={event => {
                    if (i.itemId === "menu-item-logout") {
                      dispatch(logoutUser());
                      // Close mobile menu after logout
                      if (mobileOpen) {
                        onDrawerToggle();
                      }
                    } else {
                      setSelected(i.itemId);

                      if (!i.collapsable) {
                        handleItemClick(i.redirectTo, event);
                        // Close mobile menu after navigation on mobile devices
                        if (mobileOpen) {
                          onDrawerToggle();
                        }
                      }
                    }
                  }}
                  setSelected={setSelected}
                  selected={selected}
                  items={i.subMenuItems}
                  collapsable={i.collapsable}
                  onNavigate={mobileOpen ? onDrawerToggle : null}
                />
              ))}
          </MenuList>
      </div>
    </div>
  );
  return (
    <nav className="drawer" aria-label="Main navigation">
      {/* Mobile drawer - temporary, ONLY on screens < 600px */}
      <Drawer
        id="mobile-navigation-drawer"
        container={container}
        variant="temporary"
        open={mobileOpen}
        onClose={onDrawerToggle}
        classes={{
          paper: "drawerPaper"
        }}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
          'aria-labelledby': 'mobile-menu-title'
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          [`& .MuiDrawer-paper`]: {
            width: "240px",
            boxSizing: "border-box"
          }
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop drawer - permanent, ONLY on screens >= 600px */}
      <Drawer
        container={container}
        variant="permanent"
        classes={{
          paper: "drawerPaper"
        }}
        PaperProps={{
          role: "navigation",
          'aria-label': "Main navigation"
        }}
        sx={{
          display: { xs: 'none', sm: 'block' },
          width: "240px",
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: "240px",
            boxSizing: "border-box"
          }
        }}
      >
        {drawer}
      </Drawer>
    </nav>
  );
};

export default SideBar;

SideBar.propTypes = {
  window: PropTypes.object,
  mobileOpen: PropTypes.bool.isRequired,
  onDrawerToggle: PropTypes.func.isRequired
};
